import React, { createContext, useContext, useState, useEffect } from "react";

const CampaignPreferencesContext = createContext();

export const useCampaignPreferences = () => {
  const context = useContext(CampaignPreferencesContext);
  if (!context) {
    throw new Error(
      "useCampaignPreferences must be used within a CampaignPreferencesProvider"
    );
  }
  return context;
};

export const CampaignPreferencesProvider = ({ children }) => {
  const [campaignFilters, setCampaignFilters] = useState({
    leadType: "all",
    dateRange: "all",
    leadsCountRange: "all",
    sortBy: "newest",
  });

  const [leadFilters, setLeadFilters] = useState({
    status: "all",
    fitScore: "all",
    location: "all",
    company: "all",
    experience: "all",
    sortBy: "fitScore",
  });

  const [tablePreferences, setTablePreferences] = useState({
    visibleColumns: {
      select: true,
      name: true,
      jobTitle: true,
      company: true,
      email: true,
      phone: true,
      mobile: false, // NEW: Mobile phone (hidden by default to save space)
      status: true,
      location: true,
      experience: false, // HIDDEN: Remove experience column
      managementLevel: true, // ENABLED: Show management level by default
      fitScore: false, // HIDDEN: Remove fit score column
      companyLogo: false, // HIDDEN: Remove company logo column
      companyJobs: true, // ENABLED: Company jobs (visible by default)
      linkedin: true,
      dateAdded: true,
    },
    expandedRows: new Set(),
    viewMode: "table",
  });

  const [searchTerms, setSearchTerms] = useState({
    campaigns: "",
    leads: "",
  });

  // Load preferences from localStorage on mount
  useEffect(() => {
    const loadPreferences = () => {
      try {
        const savedCampaignFilters = localStorage.getItem(
          "hotlead_campaign_filters"
        );
        if (savedCampaignFilters) {
          setCampaignFilters(JSON.parse(savedCampaignFilters));
        }

        const savedLeadFilters = localStorage.getItem("hotlead_lead_filters");
        if (savedLeadFilters) {
          setLeadFilters(JSON.parse(savedLeadFilters));
        }

        const savedTablePreferences = localStorage.getItem(
          "hotlead_table_preferences"
        );
        if (savedTablePreferences) {
          const parsed = JSON.parse(savedTablePreferences);
          setTablePreferences({
            ...parsed,
            expandedRows: new Set(parsed.expandedRows || []),
          });
        }

        const savedSearchTerms = localStorage.getItem("hotlead_search_terms");
        if (savedSearchTerms) {
          setSearchTerms(JSON.parse(savedSearchTerms));
        }
      } catch (error) {
        console.error("Error loading preferences from localStorage:", error);
      }
    };

    loadPreferences();
  }, []);

  // Save preferences to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(
        "hotlead_campaign_filters",
        JSON.stringify(campaignFilters)
      );
    } catch (error) {
      console.error("Error saving campaign filters to localStorage:", error);
    }
  }, [campaignFilters]);

  useEffect(() => {
    try {
      localStorage.setItem("hotlead_lead_filters", JSON.stringify(leadFilters));
    } catch (error) {
      console.error("Error saving lead filters to localStorage:", error);
    }
  }, [leadFilters]);

  useEffect(() => {
    try {
      const preferencesToSave = {
        ...tablePreferences,
        expandedRows: Array.from(tablePreferences.expandedRows),
      };
      localStorage.setItem(
        "hotlead_table_preferences",
        JSON.stringify(preferencesToSave)
      );
    } catch (error) {
      console.error("Error saving table preferences to localStorage:", error);
    }
  }, [tablePreferences]);

  useEffect(() => {
    try {
      localStorage.setItem("hotlead_search_terms", JSON.stringify(searchTerms));
    } catch (error) {
      console.error("Error saving search terms to localStorage:", error);
    }
  }, [searchTerms]);

  // Helper functions
  const updateCampaignFilters = (newFilters) => {
    setCampaignFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const updateLeadFilters = (newFilters) => {
    setLeadFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const updateTablePreferences = (newPreferences) => {
    setTablePreferences((prev) => ({ ...prev, ...newPreferences }));
  };

  const updateSearchTerms = (newTerms) => {
    setSearchTerms((prev) => ({ ...prev, ...newTerms }));
  };

  const resetCampaignFilters = () => {
    setCampaignFilters({
      leadType: "all",
      dateRange: "all",
      leadsCountRange: "all",
      sortBy: "newest",
    });
  };

  const resetLeadFilters = () => {
    setLeadFilters({
      status: "all",
      fitScore: "all",
      location: "all",
      company: "all",
      experience: "all",
      sortBy: "fitScore",
    });
  };

  const resetTablePreferences = () => {
    setTablePreferences({
      visibleColumns: {
        select: true,
        name: true,
        jobTitle: true,
        company: true,
        email: true,
        phone: true,
        mobile: false, // NEW: Mobile phone (hidden by default to save space)
        status: true,
        location: true,
        experience: false, // HIDDEN: Remove experience column
        managementLevel: true, // ENABLED: Show management level by default
        fitScore: false, // HIDDEN: Remove fit score column
        companyLogo: false, // HIDDEN: Remove company logo column
        companyJobs: true, // ENABLED: Company jobs (visible by default)
        linkedin: true,
        dateAdded: true,
      },
      expandedRows: new Set(),
      viewMode: "table",
    });
  };

  const resetSearchTerms = () => {
    setSearchTerms({
      campaigns: "",
      leads: "",
    });
  };

  const resetAllPreferences = () => {
    resetCampaignFilters();
    resetLeadFilters();
    resetTablePreferences();
    resetSearchTerms();

    // Clear localStorage
    try {
      localStorage.removeItem("hotlead_campaign_filters");
      localStorage.removeItem("hotlead_lead_filters");
      localStorage.removeItem("hotlead_table_preferences");
      localStorage.removeItem("hotlead_search_terms");
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  };

  const toggleRowExpansion = (leadId) => {
    setTablePreferences((prev) => {
      const newExpanded = new Set(prev.expandedRows);
      if (newExpanded.has(leadId)) {
        newExpanded.delete(leadId);
      } else {
        newExpanded.add(leadId);
      }
      return {
        ...prev,
        expandedRows: newExpanded,
      };
    });
  };

  const toggleColumnVisibility = (columnId) => {
    setTablePreferences((prev) => ({
      ...prev,
      visibleColumns: {
        ...prev.visibleColumns,
        [columnId]: !prev.visibleColumns[columnId],
      },
    }));
  };

  const value = {
    // State
    campaignFilters,
    leadFilters,
    tablePreferences,
    searchTerms,

    // Update functions
    updateCampaignFilters,
    updateLeadFilters,
    updateTablePreferences,
    updateSearchTerms,

    // Reset functions
    resetCampaignFilters,
    resetLeadFilters,
    resetTablePreferences,
    resetSearchTerms,
    resetAllPreferences,

    // Helper functions
    toggleRowExpansion,
    toggleColumnVisibility,

    // Convenience setters
    setCampaignFilters,
    setLeadFilters,
    setTablePreferences,
    setSearchTerms,
  };

  return (
    <CampaignPreferencesContext.Provider value={value}>
      {children}
    </CampaignPreferencesContext.Provider>
  );
};

export default CampaignPreferencesProvider;
